import { EventPhoto } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  cn,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@kalos/ui';
import { TrashIcon } from '@radix-ui/react-icons';
import { groupBySystem } from 'kalos-modules';
import { Loader2 } from 'lucide-react';
import {
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useDiagnosisBatchGetQuery } from '../../../hooks/react-query/useDiagnosisQuery';
import { useEventByIdQuery } from '../../../hooks/react-query/useEventByIdQuery';
import {
  type PhotoVisibilityFlag,
  useEventPhotoGalleryPreviews,
} from '../../../hooks/react-query/useEventPhotoGalleryPreviews';
import { useUpdateEventPhotoMutation } from '../../../hooks/react-query/useEventPhotoQuery';
import { useCreateFullMemoMutation, useMemoQuery } from '../../../hooks/react-query/useMemoQuery';
import { useServiceItemBatchGetQuery } from '../../../hooks/react-query/useServiceItemQuery';
import { PHOTO_CAPTURE_ACCEPT, runWithConcurrency } from '../../../tools/fileUploadUtils';
import { EventClientService, getMimeType } from '../../../tools/helpers';
import { withPhonePhotoGroupHeadings } from '../../../utils/phonePhotoGroupHeadings';
import {
  JobDetailsContext,
  type JobDetailsContextType,
} from '../../JobDetails/context/JobDetailsContext';
import {
  PhotoUploadForm,
  type PhotoUploadFormRef,
} from '../ServiceCall/components/NotesForm/PhotoUploadForm';
import { copyNotePhotoToDocument } from './copyNotePhotoToDocument';
import {
  buildMemoImagePreviewDataUrl,
  buildPhotoVisibilityUpdate,
  buildUploadMemoRequest,
  isNotePhotoFile,
  isValidImageFile,
  type MemoImageListItem,
  prepareUploadFileObjects,
  PROMOTE_CONCURRENCY,
  promoteUploadedFileToDocument,
} from './documentPhotoRequests';

interface DownloadedEventPhoto {
  id: number;
  fileId: number;
  fileName: string;
  mimeType: string;
  data: string;
  serviceItemId: number;
  /** Whether this photo is included on the proposal/invoice shown to the customer. */
  isShown: boolean;
}

const MemoPhotoSelectableCard: FC<{
  item: MemoImageListItem;
  isSelected: boolean;
  onToggle: () => void;
  /** Phones: a plain square tile with an "Add" mark, no caption. */
  tile?: boolean;
  /** Tapped and on its way: marked at once, without waiting for the upload. */
  pending?: boolean;
}> = ({ item, isSelected, onToggle, tile = false, pending = false }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPreviewUrl(null);
    setLoadFailed(false);
    setImgLoaded(false);

    void buildMemoImagePreviewDataUrl({ name: item.fileName, bucket: item.fileBucket }).then(
      (url) => {
        if (cancelled) return;
        if (!url) {
          setLoadFailed(true);
          return;
        }
        setPreviewUrl(url);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [item.memoId, item.fileId, item.fileName, item.fileBucket]);

  const fetching = previewUrl === null && !loadFailed;
  const interactive = Boolean(previewUrl) && imgLoaded && !loadFailed;

  return (
    <Card
      className={`${tile ? 'overflow-hidden rounded-md border-dashed p-0 shadow-none' : 'bg-white p-2'} transition-all ${
        interactive && isSelected ? 'ring-2 ring-blue-500' : ''
      } ${interactive ? 'cursor-pointer' : loadFailed ? 'cursor-default' : 'cursor-wait'}`}
      onClick={interactive && !pending ? onToggle : undefined}
      role={tile ? 'button' : undefined}
      aria-label={tile ? `Add photo from the job notes: ${item.fileName}` : undefined}
    >
      <div className="group relative">
        <div
          className={`relative flex overflow-hidden ${tile ? 'bg-muted aspect-square' : 'aspect-video rounded-t-lg bg-gray-100'}`}
        >
          {fetching && (
            <div className="absolute inset-0 z-[5] flex items-center justify-center bg-gray-100">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-hidden />
            </div>
          )}
          {loadFailed && (
            <div className="flex min-h-[120px] flex-1 items-center justify-center p-4 text-center text-xs text-gray-500">
              Couldn&apos;t load preview
            </div>
          )}
          {previewUrl ? (
            <>
              {!imgLoaded && !loadFailed && (
                <div className="absolute inset-0 z-[5] flex items-center justify-center bg-gray-100">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-hidden />
                </div>
              )}
              <img
                src={previewUrl}
                alt={item.fileName}
                onLoad={() => setImgLoaded(true)}
                onError={() => setLoadFailed(true)}
                className={`h-full w-full object-cover ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
              />
            </>
          ) : null}
        </div>
        {interactive && tile && (
          <span
            className={`absolute bottom-1 right-1 z-10 rounded-full px-2 py-0.5 text-xs font-medium shadow-sm ${pending ? 'bg-primary text-primary-foreground' : 'bg-card'}`}
          >
            {pending ? 'Adding…' : 'Add'}
          </span>
        )}
        {interactive && !tile && (
          <div className="absolute right-2 top-2 z-10">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggle()}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
      <CardContent className={tile ? 'hidden' : 'bg-white p-2'}>
        <p className="truncate text-xs">{item.memoDescription}</p>
        <p className="mt-1 text-xs text-gray-500">{new Date(item.memoDate).toLocaleDateString()}</p>
      </CardContent>
    </Card>
  );
};

const PHOTO_SKELETON_DEFAULT_COUNT = 6;

/** Phones: how many of the document's photos show before "Show all" (two rows of four). */
const COMPACT_PHOTOS_SHOWN = 8;

/** Phones: how many job-note photos show before "Show all", the same as above. */
const NOTE_PHOTOS_SHOWN = 8;

/** Placeholder tiles with staggered pulse so loading feels faster than one global spinner. */
const PhotoGridSkeleton: FC<{ count?: number }> = ({ count = PHOTO_SKELETON_DEFAULT_COUNT }) => (
  <div className="grid grid-cols-2 gap-2 p-1 pb-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
    {Array.from({ length: count }, (_, i) => (
      <Card key={`photo-skeleton-${i}`} className="overflow-hidden bg-white p-2 shadow-sm">
        <div
          className="aspect-video animate-pulse rounded-t-lg bg-gray-200"
          style={{ animationDelay: `${i * 95}ms` }}
        />
        <CardContent className="space-y-2 bg-white p-2 pt-3">
          <div
            className="h-3 max-w-[85%] animate-pulse rounded bg-gray-200"
            style={{ animationDelay: `${i * 95 + 45}ms` }}
          />
          <div
            className="h-2 max-w-[45%] animate-pulse rounded bg-gray-100"
            style={{ animationDelay: `${i * 95 + 90}ms` }}
          />
        </CardContent>
      </Card>
    ))}
  </div>
);

const SelectablePhotoCard: FC<{
  imageSrc: string;
  imageAlt: string;
  allowSelection: boolean;
  isSelected: boolean;
  onToggle: () => void;
  showCheckbox: boolean;
  ringVariant: 'red' | 'blue' | 'primary';
  /** object-contain (invoice gallery) vs object-cover (note thumbnails) */
  contain?: boolean;
  /** Grey the thumbnail to signal it is not shown to the customer. */
  dimmed?: boolean;
  footer: ReactNode;
  /** Phones: a plain square tile, the same size as the job-note photos beside it. */
  tile?: boolean;
}> = ({
  imageSrc,
  imageAlt,
  allowSelection,
  isSelected,
  onToggle,
  showCheckbox,
  ringVariant,
  contain = false,
  dimmed = false,
  footer,
  tile = false,
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const interactive = allowSelection && imgLoaded;
  const ring =
    ringVariant === 'red'
      ? 'ring-red-500'
      : ringVariant === 'primary'
        ? 'ring-primary'
        : 'ring-blue-500';

  return (
    <Card
      className={`${tile ? 'overflow-hidden rounded-md p-0 shadow-none' : 'bg-white p-2'} transition-all ${
        interactive && isSelected ? `ring-2 ${ring}` : ''
      } ${interactive ? 'cursor-pointer' : allowSelection ? 'cursor-wait' : ''}`}
      onClick={interactive ? onToggle : undefined}
    >
      <div className="group relative">
        <div
          className={`relative flex overflow-hidden transition-opacity ${tile ? 'bg-muted aspect-square' : 'aspect-video rounded-t-lg bg-gray-100'} ${
            contain ? 'items-center justify-center' : ''
          } ${dimmed ? 'opacity-40 grayscale' : ''}`}
        >
          {!imgLoaded && (
            <div className="absolute inset-0 z-[5] flex items-center justify-center bg-gray-100">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" aria-hidden />
            </div>
          )}
          <img
            src={imageSrc}
            alt={imageAlt}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgLoaded(true)}
            className={`${
              contain ? 'max-h-full max-w-full object-contain' : 'h-full w-full object-cover'
            } ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>
        {showCheckbox && imgLoaded && (
          <div className="absolute right-2 top-2 z-10">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggle()}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
      {footer}
    </Card>
  );
};

export interface ProposalInvoicePhotoSelectorProps {
  /** The event/job ID */
  eventId: number;
  /** Filter object - should contain either quoteDocumentId or invoiceId */
  filter: Partial<EventPhoto>;
  /** The S3 bucket to store photos in */
  bucket: string;
  /** Label for the section */
  label?: string;
  /** The logged in user's ID */
  loggedUserId: number;
  /** Optional callback when photos change */
  onPhotosChanged?: () => void;
  /** If true, only displays photos without ability to add/delete (unless allowDelete) */
  displayOnly?: boolean;
  /** With displayOnly: allow multi-select and batch delete on the gallery */
  allowDelete?: boolean;
  /**
   * Force the shown-to-customer toggle on the current-photos grid regardless of displayOnly.
   * Lets a displayOnly gallery (e.g. Services Rendered) manage include-on-invoice visibility
   * without also exposing the add/upload tabs. Defaults to `!displayOnly` when unset.
   */
  manageVisibility?: boolean;
  /**
   * Hide the "Current Photos" tab — only "Add From Notes" + upload form (e.g. Add Photo modal).
   */
  hideCurrentPhotosTab?: boolean;
  /** Optional callback when busy state changes (uploading, processing, etc.) */
  onBusyStateChange?: (isBusy: boolean) => void;
  /** Increment when opening Add Photo (etc.) to clear selections on galleries using this signal */
  clearGallerySelectionSignal?: number;
  /**
   * Which destination the "shown to customer" toggle publishes to. Defaults to
   * whatever `filter` implies — invoiceId means invoice, otherwise proposal — so
   * existing callers are unchanged.
   *
   * ServiceChannel must state it, because its photos are just the job's photos:
   * the filter is `{ eventId }` and there is no document to infer from.
   */
  visibilityTarget?: PhotoVisibilityFlag;

  /**
   * Extra destinations the same toggle publishes to, kept in step with the
   * primary one.
   *
   * For a ServiceChannel job the client and the subscriber are the same
   * organisation seeing the same photos through two surfaces, so asking a
   * technician the question twice invites the two answers to disagree — and a
   * photo held back from the invoice but not the work order is the disagreement
   * that matters. One checkbox, both columns. Split them into their own control
   * if a real need for two answers ever turns up.
   */
  mirrorVisibilityTo?: PhotoVisibilityFlag[];

  /**
   * Phones: the grid without its own header or file names, the count as one quiet line and
   * the selection in the primary colour, for a card that already has a title.
   */
  compact?: boolean;

  /**
   * Phones: the id of a hidden photo input this renders, so a card's own "Add photos" can be a
   * label for it. One tap opens the phone's picker (camera or library, several at once) and the
   * picks upload as a job note, like the upload form.
   */
  uploadInputId?: string;

  /**
   * Phones: the job notes' photos that aren't on this document yet, under the photos that
   * are, each added with one tap. No separate pick view, no Add selected.
   */
  addFromNotesInline?: boolean;
}

export const ProposalInvoicePhotoSelector: FC<ProposalInvoicePhotoSelectorProps> = ({
  eventId,
  filter,
  bucket,
  label = 'Photos',
  loggedUserId,
  onPhotosChanged,
  displayOnly = false,
  allowDelete = false,
  manageVisibility,
  hideCurrentPhotosTab = false,
  onBusyStateChange,
  clearGallerySelectionSignal,
  visibilityTarget,
  mirrorVisibilityTo,
  compact = false,
  uploadInputId,
  addFromNotesInline = false,
}) => {
  const [selectedPhotos, setSelectedPhotos] = useState<Record<string, boolean>>({});
  const [showAllNotePhotos, setShowAllNotePhotos] = useState(false);
  // Phones: tap as many job-note photos as you like, in any order. Each is marked at once and
  // the taps made close together go up as one batch, after any batch already on its way.
  const [queuedNotePhotoKeys, setQueuedNotePhotoKeys] = useState<Set<string>>(new Set());
  const notePhotoQueueRef = useRef<MemoImageListItem[]>([]);
  const notePhotoTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const notePhotoChainRef = useRef<Promise<void>>(Promise.resolve());
  const [selectedCurrentPhotos, setSelectedCurrentPhotos] = useState<Record<number, boolean>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'current' | 'add'>(() =>
    hideCurrentPhotosTab ? 'add' : 'current',
  );
  // Editor context (proposal/invoice) or an explicit opt-in: the checkbox toggles whether a photo
  // is shown to the customer (includeInProposal / includeInInvoice), hidden ones render greyed.
  const canToggleShown = manageVisibility ?? !displayOnly;
  const canEditCurrentGallery = !displayOnly || allowDelete || canToggleShown;
  // Legacy transient multi-select + hard delete (only when the shown toggle isn't in play).
  const canDeletePhotos = displayOnly && allowDelete && !canToggleShown;
  const tabsValue: 'current' | 'add' = hideCurrentPhotosTab ? 'add' : activeTab;

  useEffect(() => {
    if (hideCurrentPhotosTab) setActiveTab('add');
  }, [hideCurrentPhotosTab]);

  useEffect(() => {
    if (clearGallerySelectionSignal === undefined || clearGallerySelectionSignal < 1) return;
    setSelectedCurrentPhotos({});
    setSelectedPhotos({});
  }, [clearGallerySelectionSignal]);

  const [uploadFormPhotos, setUploadFormPhotos] = useState<File[]>([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [isConvertingPhotos, setIsConvertingPhotos] = useState(false);

  const updatePhotoMutation = useUpdateEventPhotoMutation();
  const { mutateAsync: createMemo } = useCreateFullMemoMutation();

  const jobDetailsCtx = useContext(JobDetailsContext);
  const jobDetailsGlobalLoaderHeldRef = useRef(false);
  const setGlobalLoadingRef = useRef<JobDetailsContextType['setGlobalLoading'] | undefined>(
    undefined,
  );

  useEffect(() => {
    setGlobalLoadingRef.current = jobDetailsCtx?.setGlobalLoading;
  }, [jobDetailsCtx]);

  const holdJobDetailsGlobalLoader = useCallback(
    (message?: string) => {
      if (!jobDetailsCtx) return;
      jobDetailsCtx.setGlobalLoading(true, message);
      jobDetailsGlobalLoaderHeldRef.current = true;
    },
    [jobDetailsCtx],
  );

  const releaseJobDetailsGlobalLoader = useCallback(() => {
    if (!jobDetailsGlobalLoaderHeldRef.current) return;
    jobDetailsCtx?.setGlobalLoading(false);
    jobDetailsGlobalLoaderHeldRef.current = false;
  }, [jobDetailsCtx]);

  useEffect(
    () => () => {
      if (jobDetailsGlobalLoaderHeldRef.current) {
        setGlobalLoadingRef.current?.(false);
        jobDetailsGlobalLoaderHeldRef.current = false;
      }
    },
    [],
  );

  const photoUploadFormRef = useRef<PhotoUploadFormRef>(null);

  // Determine context. The two booleans still scope the *query*; visibilityFlag
  // decides which column the shown-to-customer toggle writes, which is a separate
  // question once there are more than two destinations.
  const isProposalContext = !!filter.quoteDocumentId;
  const isInvoiceContext = !!filter.invoiceId;
  const visibilityFlag: PhotoVisibilityFlag =
    visibilityTarget ?? (isInvoiceContext ? 'includeInInvoice' : 'includeInProposal');
  const isEventScopedVisibility =
    !!visibilityTarget && !filter.quoteDocumentId && !filter.invoiceId;

  const {
    rows: eventPhotoPreviewRows,
    fileIds,
    eventPhotoQuery,
    filesQuery,
    refetchQueries: refetchEventPhotoGallery,
    removeRowsByEventPhotoId,
    setRowInclude,
    isDownloadingPreviews,
  } = useEventPhotoGalleryPreviews({
    filter,
    enabled: isProposalContext || isInvoiceContext || isEventScopedVisibility,
    keepRowsWithoutPreview: false,
    includeHidden: canToggleShown,
    visibilityFlag,
  });

  const downloadedEventPhotos = useMemo((): DownloadedEventPhoto[] => {
    return eventPhotoPreviewRows
      .filter((r): r is typeof r & { dataUrl: string } => r.dataUrl != null)
      .map((r) => ({
        id: r.eventPhotoId,
        fileId: r.fileId,
        fileName: r.file.name,
        mimeType: r.file.mimeType,
        data: r.dataUrl,
        serviceItemId: r.serviceItemId,
        isShown: r[visibilityFlag],
      }));
  }, [eventPhotoPreviewRows, visibilityFlag]);

  const shownCount = downloadedEventPhotos.filter((p) => p.isShown).length;

  // Fetch memos with files for this job
  const memoQuery = useMemoQuery({
    filters: {
      eventId,
      isActive: true,
      orderBy: 'created_date',
      orderDir: 'DESC',
      withoutLimit: true,
    },
    enabled: true,
  });

  // Resolve which system a photo documents: only diagnostic notes carry a
  // system, via memo.diagnosisId -> Diagnosis.serviceItemId. A file from such a
  // memo inherits that system so the created EventPhoto groups under it.
  const diagnosisQuery = useDiagnosisBatchGetQuery({
    filter: { eventId, withoutLimit: true },
    enabled: !!eventId,
  });
  const fileIdToServiceItemId = useMemo(() => {
    const serviceItemByDiagnosisId = new Map<number, number>();
    for (const diagnosis of diagnosisQuery.data?.results ?? []) {
      if (diagnosis.id && diagnosis.serviceItemId) {
        serviceItemByDiagnosisId.set(diagnosis.id, diagnosis.serviceItemId);
      }
    }
    const map = new Map<number, number>();
    for (const memo of memoQuery.data?.results ?? []) {
      if (memo.classificationId !== 9 || !memo.diagnosisId) continue;
      const serviceItemId = serviceItemByDiagnosisId.get(memo.diagnosisId);
      if (!serviceItemId) continue;
      for (const file of memo.files?.results ?? []) {
        if (file.id) map.set(file.id, serviceItemId);
      }
    }
    return map;
  }, [diagnosisQuery.data, memoQuery.data]);

  // Resolve service-item display names so current photos can be grouped by system.
  // Outside JobDetails (legacy proposal dialog, invoice steps) there is no context,
  // so fall back to the event's property.
  const contextPropertyId = jobDetailsCtx?.property?.id ?? 0;
  const eventQuery = useEventByIdQuery({ eventId, enabled: contextPropertyId === 0 });
  const propertyId = contextPropertyId || (eventQuery.data?.propertyId ?? 0);
  const serviceItemsQuery = useServiceItemBatchGetQuery({
    filter: { propertyId, isActive: 1, withoutLimit: true },
    enabled: propertyId > 0,
  });
  const serviceItemNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const si of serviceItemsQuery.data?.results ?? []) {
      const label = [si.location?.trim(), si.type?.trim()].filter(Boolean).join(' ').trim();
      map.set(si.id, label || si.item?.trim() || '');
    }
    return map;
  }, [serviceItemsQuery.data]);

  // Group current photos by system; unassigned -> "Additional photos" last.
  // Phones: the photos going to the customer first, and only a couple of rows until Show all,
  // so a job with dozens of photos isn't a long scroll to get past.
  const [showAllCurrentPhotos, setShowAllCurrentPhotos] = useState(false);
  const compactPhotos = useMemo(() => {
    if (!compact) return downloadedEventPhotos;
    const ordered = [
      ...downloadedEventPhotos.filter((photo) => photo.isShown),
      ...downloadedEventPhotos.filter((photo) => !photo.isShown),
    ];
    return showAllCurrentPhotos ? ordered : ordered.slice(0, COMPACT_PHOTOS_SHOWN);
  }, [compact, downloadedEventPhotos, showAllCurrentPhotos]);
  const hiddenPhotoCount = compact ? downloadedEventPhotos.length - compactPhotos.length : 0;

  const currentPhotoGroups = useMemo(() => {
    const groups = groupBySystem(
      compactPhotos,
      (photo) => photo.serviceItemId,
      (photo) => serviceItemNameById.get(photo.serviceItemId) ?? '',
    );
    return compact ? withPhonePhotoGroupHeadings(groups) : groups;
  }, [compact, compactPhotos, serviceItemNameById]);

  const memoImageItems = useMemo(() => {
    const results = memoQuery.data?.results;
    if (!results?.length) return [];
    const list: MemoImageListItem[] = [];
    for (const memo of results) {
      if (!memo.files?.results?.length) continue;
      for (const file of memo.files.results) {
        const baseMimeType = file.mimeType || getMimeType(file.name);
        if (baseMimeType === 'application/pdf') continue;
        if (!baseMimeType.includes('image')) continue;
        list.push({
          memoId: memo.id,
          fileId: file.id,
          fileName: file.name,
          fileBucket: file.bucket,
          fileMimeType: baseMimeType,
          memoDescription: memo.description || '',
          memoDate: memo.createdDate,
        });
      }
    }
    return list;
  }, [memoQuery.data?.results]);

  // Group note photos by system; unassigned -> "Additional photos" last.
  const memoImageGroups = useMemo(() => {
    const groups = groupBySystem(
      memoImageItems,
      (item) => fileIdToServiceItemId.get(item.fileId) ?? 0,
      (item) => serviceItemNameById.get(fileIdToServiceItemId.get(item.fileId) ?? 0) ?? '',
    );
    return compact ? withPhonePhotoGroupHeadings(groups) : groups;
  }, [compact, memoImageItems, fileIdToServiceItemId, serviceItemNameById]);

  // Note photos not on this document yet. Adding copies the file under a new name that ends with
  // the original's (see handleAddSelectedPhotos), so a photo counts as added when one of this
  // document's files is that file or carries its name. Checked against the document's file list,
  // not the downloaded previews, so a photo whose preview is still loading isn't offered again.
  const documentFiles = filesQuery.data;
  const notesPhotosNotAdded = useMemo(() => {
    const files = (documentFiles ?? []).filter(
      (file): file is NonNullable<typeof file> => !!file?.id,
    );
    return memoImageItems.filter((item) => !files.some((file) => isNotePhotoFile(item, file)));
  }, [memoImageItems, documentFiles]);

  const noteKey = (item: MemoImageListItem) => `${item.memoId}-${item.fileId}`;
  // Read through a ref as well as state, so a fast double tap can't queue a photo twice.
  const queuedNotePhotoKeysRef = useRef(new Set<string>());
  const queueNotePhoto = (item: MemoImageListItem) => {
    const key = noteKey(item);
    if (queuedNotePhotoKeysRef.current.has(key)) return;
    queuedNotePhotoKeysRef.current.add(key);
    setQueuedNotePhotoKeys((previous) => new Set(previous).add(key));
    notePhotoQueueRef.current.push(item);
    if (notePhotoTimerRef.current) clearTimeout(notePhotoTimerRef.current);
    notePhotoTimerRef.current = setTimeout(() => {
      const batch = notePhotoQueueRef.current;
      notePhotoQueueRef.current = [];
      notePhotoChainRef.current = notePhotoChainRef.current.then(async () => {
        await handleAddSelectedPhotos(batch);
        batch.forEach((done) => queuedNotePhotoKeysRef.current.delete(noteKey(done)));
        setQueuedNotePhotoKeys((previous) => {
          const next = new Set(previous);
          batch.forEach((done) => next.delete(noteKey(done)));
          return next;
        });
      });
    }, 700);
  };
  useEffect(
    () => () => {
      if (notePhotoTimerRef.current) clearTimeout(notePhotoTimerRef.current);
    },
    [],
  );

  // Notify parent of busy state changes
  useEffect(() => {
    const isBusy = isProcessing || isDeleting || isUploadingPhotos || isConvertingPhotos;
    onBusyStateChange?.(isBusy);
  }, [isProcessing, isDeleting, isUploadingPhotos, isConvertingPhotos, onBusyStateChange]);

  const handleTabChange = (tab: string) => {
    if (hideCurrentPhotosTab) return;
    // Prevent tab switching during processing operations
    if (isDeleting || isProcessing || isUploadingPhotos || isConvertingPhotos) {
      return;
    }

    setActiveTab(tab as 'current' | 'add');
  };

  const handlePhotoUploadSuccess = () => {
    // Refetch the memo query to get the newly uploaded photos
    memoQuery.refetch();
    // Clear the upload form photos
    setUploadFormPhotos([]);
    // Stay on current tab, don't close dialog
  };

  const handleUploadFormPhotosChange = (files: File[]) => {
    setUploadFormPhotos(files);
  };

  const photoTarget = { eventId, filter, bucket };

  const handleUploadFormSubmit = async (files: File[]) => {
    setIsUploadingPhotos(true);
    let progressToast: ReturnType<typeof toast> | null = null;
    try {
      // Validate all files before processing
      const validationPromises = files.map(async (file) => {
        const isValid = await isValidImageFile(file);
        return { file, isValid };
      });

      const validationResults = await Promise.all(validationPromises);
      const validFiles = validationResults
        .filter((result) => result.isValid)
        .map((result) => result.file);
      const invalidFiles = validationResults
        .filter((result) => !result.isValid)
        .map((result) => result.file);

      // If all files are invalid, show error and don't create memo
      if (validFiles.length === 0) {
        toast({
          title: 'Upload Failed',
          description:
            'Invalid File Type. Please upload valid image files (JPEG, PNG, WebP, HEIC).',
          variant: 'destructive',
        });
        return;
      }

      // If some files are invalid, show warning but proceed with valid files
      if (invalidFiles.length > 0) {
        toast({
          title: 'Some files skipped',
          description: `${invalidFiles.length} file(s) were skipped due to invalid file type. Only valid image files will be uploaded.`,
          variant: 'destructive',
        });
      }

      holdJobDetailsGlobalLoader('Uploading photos…');

      progressToast = toast({
        title: 'Preparing photos…',
        description: `Optimizing ${validFiles.length} image(s) for upload.`,
      });

      const imageFileObjects = await prepareUploadFileObjects(validFiles);

      progressToast.update({
        id: progressToast.id,
        title: 'Uploading…',
        description: 'Sending photos to the server…',
      });

      const request = buildUploadMemoRequest({
        validFileCount: validFiles.length,
        imageFileObjects,
        loggedUserId,
        eventId,
      });

      const createdMemo = await createMemo(request);

      const createdPhotos: EventPhoto[] = [];
      const errors: string[] = [];

      if (!createdMemo.files?.results || createdMemo.files.results.length === 0) {
        toast({
          title: 'Upload Failed',
          description: 'No files were created in the memo.',
          variant: 'destructive',
        });
        return;
      }

      progressToast.update({
        id: progressToast.id,
        title: 'Saving…',
        description: 'Attaching photos to the job…',
      });

      const results = await runWithConcurrency(
        createdMemo.files.results,
        PROMOTE_CONCURRENCY,
        async (file) => {
          try {
            return await promoteUploadedFileToDocument(file, photoTarget);
          } catch (e) {
            const errorMessage = e instanceof Error ? e.message : 'Unknown error';
            errors.push(errorMessage);
            return null;
          }
        },
      );

      createdPhotos.push(...results.filter((photo): photo is EventPhoto => photo !== null));

      progressToast.dismiss();
      progressToast = null;

      if (createdPhotos.length > 0) {
        if (!hideCurrentPhotosTab) setActiveTab('current');

        // Parents (e.g. Services Rendered dialog) dismiss first so success toast is not covered.
        onPhotosChanged?.();

        toast({
          title: 'Photos Added',
          description: `Successfully uploaded and added ${createdPhotos.length} photo(s).`,
          variant: 'success',
        });

        await refetchEventPhotoGallery();

        handlePhotoUploadSuccess();
      }

      if (errors.length > 0) {
        toast({
          title: `${errors.length} photo(s) failed to upload`,
          description: 'Some photos could not be added.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast({
        title: 'Upload Failed',
        description: 'An error occurred while uploading photos.',
        variant: 'destructive',
      });
    } finally {
      releaseJobDetailsGlobalLoader();
      progressToast?.dismiss();
      setIsUploadingPhotos(false);
    }
  };

  const clearUploadFormPhotos = () => {
    setUploadFormPhotos([]);
    photoUploadFormRef.current?.clearFiles(); // Clear the actual form
  };

  const togglePhotoSelection = (photoKey: string) => {
    setSelectedPhotos((prev) => ({
      ...prev,
      [photoKey]: !prev[photoKey],
    }));
  };

  const toggleCurrentPhotoSelection = (photoId: number) => {
    setSelectedCurrentPhotos((prev) => ({
      ...prev,
      [photoId]: !prev[photoId],
    }));
  };

  // The primary destination plus any mirrors, deduped so a caller naming the
  // same flag twice writes it once.
  const visibilityFlags: PhotoVisibilityFlag[] = Array.from(
    new Set<PhotoVisibilityFlag>([visibilityFlag, ...(mirrorVisibilityTo ?? [])]),
  );

  const buildIncludeUpdate = (photoId: number, shown: boolean) =>
    buildPhotoVisibilityUpdate(photoId, shown, visibilityFlags);

  const includeFlagPatch = (shown: boolean) =>
    Object.fromEntries(visibilityFlags.map((flag) => [flag, shown]));

  // Toggle whether a photo is shown to the customer. Optimistic: the thumbnail greys/ungreys
  // immediately, the include flag persists in the background, and we revert on failure. This
  // calls the service directly (not updatePhotoMutation) so React Query never invalidates the
  // gallery — otherwise every toggle would blank the grid and re-download previews.
  const handleTogglePhotoShown = (photoId: number, nextShown: boolean) => {
    setRowInclude(photoId, includeFlagPatch(nextShown));
    EventClientService.UpdateEventPhoto(buildIncludeUpdate(photoId, nextShown)).catch((error) => {
      console.error('Error updating photo visibility:', error);
      setRowInclude(photoId, includeFlagPatch(!nextShown));
      toast({
        title: 'Update Failed',
        description: 'Could not update whether this photo is shown to the customer.',
        variant: 'destructive',
      });
    });
  };

  // Hide every currently-shown photo (reversible — records are kept, just not shown).
  // Optimistic + direct service call, same reasoning as handleTogglePhotoShown.
  const handleHideAllShown = () => {
    const shownIds = downloadedEventPhotos.filter((p) => p.isShown).map((p) => p.id);
    if (shownIds.length === 0) return;
    shownIds.forEach((id) => setRowInclude(id, includeFlagPatch(false)));
    Promise.all(
      shownIds.map((id) => EventClientService.UpdateEventPhoto(buildIncludeUpdate(id, false))),
    ).catch((error) => {
      console.error('Error hiding photos:', error);
      shownIds.forEach((id) => setRowInclude(id, includeFlagPatch(true)));
      toast({
        title: 'Update Failed',
        description: 'Could not update the photos shown to the customer.',
        variant: 'destructive',
      });
    });
  };

  // Handle batch photo soft-deletion (set isActive=false and respective include flag to false)
  const handleDeleteSelectedPhotos = async () => {
    const photoIdsToDelete = Object.entries(selectedCurrentPhotos)
      .filter(([_, isSelected]) => isSelected)
      .map(([photoId]) => parseInt(photoId));

    if (photoIdsToDelete.length === 0) {
      toast({
        title: 'No Photos Selected',
        description: 'Please select at least one photo to delete.',
        variant: 'destructive',
      });
      return;
    }

    setIsDeleting(true);
    holdJobDetailsGlobalLoader('Removing photos…');

    // Determine context for setting the correct include flag
    const isProposalContext = !!filter.quoteDocumentId;
    const isInvoiceContext = !!filter.invoiceId;

    // Remove deleted photos from local state immediately for better UX
    removeRowsByEventPhotoId(photoIdsToDelete);

    // Reset selection state
    setSelectedCurrentPhotos({});

    try {
      // Soft-delete each selected photo by setting isActive=false and include flag to false
      const updatePromises = photoIdsToDelete.map(async (photoId) => {
        const photoToUpdate = EventPhoto.create({
          id: photoId,
          isActive: false,
          ...(isProposalContext && { includeInProposal: false }),
          ...(isInvoiceContext && { includeInInvoice: false }),
          fieldMask: [
            'IsActive',
            ...(isProposalContext ? ['IncludeInProposal'] : []),
            ...(isInvoiceContext ? ['IncludeInInvoice'] : []),
          ],
        });

        return updatePhotoMutation.mutateAsync(photoToUpdate);
      });

      await Promise.all(updatePromises);

      // Notify parent of changes
      onPhotosChanged?.();

      toast({
        title: 'Photos Removed',
        description: `Successfully removed ${photoIdsToDelete.length} photo(s)`,
        variant: 'success',
      });
    } catch (error) {
      console.error('Error removing photos:', error);
      toast({
        title: 'Remove Failed',
        description: 'An error occurred while removing the photos',
        variant: 'destructive',
      });
    } finally {
      releaseJobDetailsGlobalLoader();
      setIsDeleting(false);
    }
  };

  // Process selected photos and add them
  const handleAddSelectedPhotos = async (only?: MemoImageListItem[]) => {
    const selectedPhotosList =
      only ?? memoImageItems.filter((photo) => selectedPhotos[`${photo.memoId}-${photo.fileId}`]);

    if (selectedPhotosList.length === 0) {
      toast({
        title: 'No Photos Selected',
        description: 'Please select at least one photo to add.',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    // One-tap adds from phones show on the tile itself; only the bulk add blocks the screen.
    if (!only) holdJobDetailsGlobalLoader('Adding photos from notes…');
    const createdPhotos: EventPhoto[] = [];
    const errors: string[] = [];

    try {
      const results = await runWithConcurrency(
        selectedPhotosList,
        PROMOTE_CONCURRENCY,
        async (photo) => {
          try {
            // If this photo originated from a diagnostic reading, tag the new
            // EventPhoto with that reading's system (keyed on the ORIGINAL source
            // fileId, not the freshly-copied fileRes.id).
            const serviceItemId = fileIdToServiceItemId.get(photo.fileId) ?? 0;
            return await copyNotePhotoToDocument(photo, { ...photoTarget, serviceItemId });
          } catch (e) {
            const errorMessage = e instanceof Error ? e.message : 'Unknown error';
            errors.push(errorMessage);
            return null;
          }
        },
      );

      createdPhotos.push(...results.filter((photo): photo is EventPhoto => photo !== null));

      if (createdPhotos.length > 0) {
        setSelectedPhotos({});

        if (!hideCurrentPhotosTab) setActiveTab('current');

        onPhotosChanged?.();

        // Phones add one photo per tap and see it appear; no toast for that.
        if (!only) {
          toast({
            title: 'Photos Added',
            description: `Successfully added ${createdPhotos.length} photo(s).`,
            variant: 'success',
          });
        }

        await refetchEventPhotoGallery();
      }

      if (errors.length > 0) {
        // Show error toast if any uploads failed
        toast({
          title: `${errors.length} photo(s) failed to upload`,
          description: 'Some photos could not be added.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error adding photos:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while adding photos.',
        variant: 'destructive',
      });
    } finally {
      // A one-tap add never held the loader, so it mustn't release one an upload is holding.
      if (!only) releaseJobDetailsGlobalLoader();
      setIsProcessing(false);
    }
  };

  const expectedEventPhotoCount = eventPhotoQuery.data?.results?.length ?? 0;
  const currentPhotosSkeletonCount = Math.min(
    Math.max(expectedEventPhotoCount || PHOTO_SKELETON_DEFAULT_COUNT, 3),
    12,
  );

  return (
    <div className="flex h-full flex-col bg-white">
      {uploadInputId && (
        <input
          id={uploadInputId}
          type="file"
          accept={PHOTO_CAPTURE_ACCEPT}
          multiple
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          disabled={isUploadingPhotos}
          onChange={(event) => {
            const picked = Array.from(event.target.files ?? []);
            event.target.value = '';
            if (picked.length > 0) void handleUploadFormSubmit(picked);
          }}
        />
      )}
      {!compact && (
        <div className="flex-none border-b bg-white px-4 py-4 sm:px-6">
          <h3 className="text-lg font-semibold">{label}</h3>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col">
        <Tabs
          value={tabsValue}
          onValueChange={handleTabChange}
          className="flex flex-1 flex-col bg-white"
        >
          {!displayOnly && !hideCurrentPhotosTab && !compact && (
            <TabsList className="flex-none border-b bg-white px-4 sm:px-6">
              <TabsTrigger value="current">All photos</TabsTrigger>
              <TabsTrigger value="add">From job notes</TabsTrigger>
            </TabsList>
          )}

          {/* Phones: no tabs. The photos, then Add photos (the phone's picker) and Pick from job
              notes as small actions; picking swaps in the job notes' photos with a Back. */}
          {compact && !displayOnly && (
            <div className="flex flex-none flex-wrap items-center gap-2 pb-2">
              {tabsValue === 'add' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="-ml-3"
                  onClick={() => handleTabChange('current')}
                >
                  Back to photos
                </Button>
              ) : (
                <>
                  {uploadInputId && (
                    <Button variant="outline" size="sm" asChild>
                      <label htmlFor={uploadInputId} className="cursor-pointer">
                        Add photos
                      </label>
                    </Button>
                  )}
                  {!addFromNotesInline && (
                    <Button variant="outline" size="sm" onClick={() => handleTabChange('add')}>
                      Pick from job notes
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Sticky action header visible when on Add tab - hidden in displayOnly mode */}
          {!displayOnly && tabsValue === 'add' && (
            <div className="sticky top-0 z-20 flex flex-none items-center justify-between border-b bg-white px-4 py-3 sm:px-6">
              {memoImageItems.length > 0 && (
                <Button
                  onClick={() => void handleAddSelectedPhotos()}
                  disabled={
                    isProcessing ||
                    isConvertingPhotos ||
                    Object.values(selectedPhotos).filter(Boolean).length === 0
                  }
                  isLoading={isProcessing}
                  size="sm"
                  className="w-full text-xs sm:w-auto sm:text-sm"
                >
                  Add Selected
                </Button>
              )}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto bg-white">
            {!hideCurrentPhotosTab && (
              <TabsContent
                value="current"
                className={cn('m-0 min-h-0 flex-1 bg-white', compact ? 'p-0' : 'p-4 sm:p-6')}
                style={{ display: activeTab === 'current' || displayOnly ? 'flex' : 'none' }}
              >
                {eventPhotoQuery.isPending ||
                (fileIds.length > 0 && filesQuery.isPending) ||
                ((activeTab === 'current' || displayOnly) && isDownloadingPreviews) ? (
                  <div className="flex min-h-0 flex-1 flex-col space-y-3 bg-white">
                    <p className="text-sm text-gray-500">Loading photos…</p>
                    <PhotoGridSkeleton count={currentPhotosSkeletonCount} />
                  </div>
                ) : downloadedEventPhotos.length === 0 && compact ? (
                  <p className="text-muted-foreground text-sm">No photos for the customer yet.</p>
                ) : downloadedEventPhotos.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center bg-white py-8 text-center text-gray-500">
                    <div>
                      <p>No photos attached.</p>
                      {!displayOnly && (
                        <p className="mt-2 text-sm">
                          Switch to the &quot;From job notes&quot; tab to add photos from job notes
                          or upload new photos.
                        </p>
                      )}
                      {displayOnly && allowDelete && (
                        <p className="mt-2 text-sm">
                          Use <span className="font-medium">Add Photo</span> to add completed work
                          photos.
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-0 flex-1 flex-col space-y-4 bg-white">
                    {canToggleShown && compact ? (
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-muted-foreground text-sm">
                          {shownCount} of {downloadedEventPhotos.length} shown to the customer
                        </p>
                        {shownCount > 0 && (
                          <Button variant="ghost" size="sm" onClick={handleHideAllShown}>
                            Leave all out
                          </Button>
                        )}
                      </div>
                    ) : canToggleShown ? (
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <h3 className="text-md font-medium">
                          {shownCount} photo{shownCount === 1 ? '' : 's'} will be shown to customer
                        </h3>
                        {shownCount > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleHideAllShown}
                            className="self-start"
                          >
                            Clear selection
                          </Button>
                        )}
                      </div>
                    ) : (
                      canDeletePhotos && (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <h3 className="text-md font-medium">
                            Current Photos ({downloadedEventPhotos.length})
                          </h3>
                          {Object.values(selectedCurrentPhotos).some(Boolean) && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={handleDeleteSelectedPhotos}
                              disabled={isDeleting}
                              isLoading={isDeleting}
                              className="self-start"
                            >
                              {!isDeleting && <TrashIcon className="mr-2 h-4 w-4" />}
                              Delete Selected (
                              {Object.values(selectedCurrentPhotos).filter(Boolean).length})
                            </Button>
                          )}
                        </div>
                      )
                    )}
                    <div className="min-h-0 flex-1 overflow-y-auto">
                      <div className="flex flex-col gap-5 p-1 pb-4">
                        {currentPhotoGroups.map((group) => (
                          <div key={group.serviceItemId}>
                            {group.label && (
                              <p className="mb-2 text-sm font-semibold">{group.label}</p>
                            )}
                            <div
                              className={
                                compact
                                  ? 'grid grid-cols-3 gap-2 sm:grid-cols-4'
                                  : 'grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
                              }
                            >
                              {group.items.map((photo) => {
                                const isSelected = canToggleShown
                                  ? photo.isShown
                                  : !!selectedCurrentPhotos[photo.id];

                                return (
                                  <SelectablePhotoCard
                                    key={photo.id}
                                    imageSrc={photo.data}
                                    imageAlt={photo.fileName}
                                    allowSelection={canEditCurrentGallery}
                                    isSelected={isSelected}
                                    onToggle={() =>
                                      canToggleShown
                                        ? handleTogglePhotoShown(photo.id, !photo.isShown)
                                        : toggleCurrentPhotoSelection(photo.id)
                                    }
                                    showCheckbox={canEditCurrentGallery}
                                    ringVariant={compact ? 'primary' : 'red'}
                                    contain={!compact}
                                    tile={compact}
                                    dimmed={canToggleShown && !photo.isShown}
                                    footer={
                                      compact ? null : (
                                        <CardContent className="bg-white p-2">
                                          <p className="truncate text-xs">{photo.fileName}</p>
                                        </CardContent>
                                      )
                                    }
                                  />
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        {compact && (hiddenPhotoCount > 0 || showAllCurrentPhotos) && (
                          <Button
                            type="button"
                            variant="outline"
                            className="self-start"
                            onClick={() => setShowAllCurrentPhotos((shown) => !shown)}
                          >
                            {showAllCurrentPhotos
                              ? 'Show fewer'
                              : `Show all ${downloadedEventPhotos.length} photos`}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
            )}

            {compact && addFromNotesInline && notesPhotosNotAdded.length > 0 && (
              // The same square tiles as above, and only the first row or two until asked:
              // a job with many photos doesn't turn this into a long scroll.
              <div className="flex flex-col gap-2 pt-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-muted-foreground text-sm">
                    From the job notes ({notesPhotosNotAdded.length}) · tap to add
                  </p>
                  {notesPhotosNotAdded.length > NOTE_PHOTOS_SHOWN && (
                    <Button
                      type="button"
                      variant="link"
                      className="text-link h-auto min-h-11 p-0 font-normal underline"
                      onClick={() => setShowAllNotePhotos((shown) => !shown)}
                    >
                      {showAllNotePhotos ? 'Show fewer' : 'Show all'}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {(showAllNotePhotos
                    ? notesPhotosNotAdded
                    : notesPhotosNotAdded.slice(0, NOTE_PHOTOS_SHOWN)
                  ).map((item) => (
                    <MemoPhotoSelectableCard
                      key={`${item.memoId}-${item.fileId}`}
                      tile
                      item={item}
                      isSelected={false}
                      pending={queuedNotePhotoKeys.has(noteKey(item))}
                      onToggle={() => queueNotePhoto(item)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Add New Photos Tab - Hidden in displayOnly mode */}
            {!displayOnly && (
              <TabsContent
                value="add"
                className="m-0 flex min-h-0 flex-1 flex-col bg-white"
                style={{ display: tabsValue === 'add' ? 'flex' : 'none' }}
              >
                <div className="flex min-h-0 flex-1 flex-col bg-white">
                  <div
                    className={cn(
                      'min-h-0 shrink-0 overflow-y-auto border-b bg-white p-4 sm:p-6',
                      // Phones add photos with the card's own Add photos.
                      compact && 'hidden',
                    )}
                  >
                    <PhotoUploadForm
                      ref={photoUploadFormRef}
                      jobId={eventId}
                      loggedUserId={loggedUserId}
                      onSuccess={handlePhotoUploadSuccess}
                      onPhotosChange={handleUploadFormPhotosChange}
                      onSubmit={handleUploadFormSubmit}
                      isUploading={isUploadingPhotos || isProcessing || isDeleting}
                      onConversionStateChange={setIsConvertingPhotos}
                      onCancel={clearUploadFormPhotos}
                    />
                  </div>

                  <div
                    className={cn(
                      'min-h-0 flex-1 overflow-y-auto bg-white',
                      compact ? 'p-0' : 'p-4 sm:p-6',
                    )}
                  >
                    <div className="space-y-4">
                      <h3 className={compact ? 'text-sm font-semibold' : 'text-lg font-medium'}>
                        {compact ? 'Photos in the job notes' : 'Available Photos From Job Notes'}
                      </h3>
                      {memoQuery.isPending ? (
                        <PhotoGridSkeleton />
                      ) : memoImageItems.length > 0 ? (
                        <div className="flex flex-col gap-5 p-1 pb-4">
                          {memoImageGroups.map((group) => (
                            <div key={group.serviceItemId}>
                              {group.label && (
                                <p className="mb-2 text-sm font-semibold">{group.label}</p>
                              )}
                              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                                {group.items.map((item) => {
                                  const photoKey = `${item.memoId}-${item.fileId}`;
                                  const isSelected = !!selectedPhotos[photoKey];

                                  return (
                                    <MemoPhotoSelectableCard
                                      key={photoKey}
                                      item={item}
                                      isSelected={isSelected}
                                      onToggle={() => togglePhotoSelection(photoKey)}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p
                          className={
                            compact ? 'text-muted-foreground text-sm' : 'text-sm text-gray-500'
                          }
                        >
                          {compact
                            ? 'No photos in the job notes yet.'
                            : 'No image attachments in job notes yet. Upload photos above or attach files to memos on the job.'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            )}
          </div>
        </Tabs>

        {/* Sticky Action Bar for Upload Form - Only show when photos are selected and not in displayOnly mode */}
        {!displayOnly && tabsValue === 'add' && uploadFormPhotos.length > 0 && (
          <div className="flex-none border-t bg-white p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button
                onClick={() => handleUploadFormSubmit(uploadFormPhotos)}
                disabled={isUploadingPhotos || isConvertingPhotos}
                className="w-full bg-gray-900 text-white hover:bg-gray-800 sm:w-auto"
                isLoading={isUploadingPhotos}
              >
                {isUploadingPhotos
                  ? 'Uploading and adding...'
                  : isConvertingPhotos
                    ? 'Converting HEIC files...'
                    : `Add ${uploadFormPhotos.length} Photo${uploadFormPhotos.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
